/**
 * CetusEd Admin Console — Sidebar Navigation
 *
 * COMPLIANCE: Navigation links are organized by admin domain. The sidebar
 *   displays the authenticated user's name and role, with a logout action.
 *   Role-based visibility is handled by capability checks.
 */

import React, { useCallback } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../auth/useAdminAuth';

interface NavSection {
  title: string;
  links: Array<{ to: string; label: string }>;
}

const NAV_SECTIONS: NavSection[] = [
  {
    title: 'Districts',
    links: [
      { to: '/districts', label: 'All Districts' },
      { to: '/districts/provision', label: 'Provision New' },
    ],
  },
  {
    title: 'Billing',
    links: [
      { to: '/billing', label: 'Dashboard' },
      { to: '/billing/invoices', label: 'Invoices' },
      { to: '/billing/plans', label: 'Plan Management' },
    ],
  },
  {
    title: 'Monitoring',
    links: [
      { to: '/monitoring', label: 'System Health' },
      { to: '/monitoring/alerts', label: 'Alerts' },
      { to: '/monitoring/audit-log', label: 'Audit Log' },
      { to: '/monitoring/rate-limits', label: 'Rate Limits' },
    ],
  },
  {
    title: 'Integrations',
    links: [
      { to: '/integrations/sso', label: 'SSO' },
      { to: '/integrations/sis', label: 'SIS Sync' },
      { to: '/integrations/webhooks', label: 'Webhooks' },
      { to: '/integrations/email', label: 'Email' },
    ],
  },
  {
    title: 'Support',
    links: [
      { to: '/support/tickets', label: 'Tickets' },
      { to: '/support/impersonate', label: 'Impersonate' },
      { to: '/support/announcements', label: 'Announcements' },
    ],
  },
];

export const Sidebar: React.FC = () => {
  const auth = useAdminAuth();
  const navigate = useNavigate();

  const handleLogout = useCallback(() => {
    auth.logout();
    navigate('/login', { replace: true });
  }, [auth, navigate]);

  return (
    <aside className="admin-sidebar">
      <div className="sidebar-brand">
        <span className="sidebar-logo">CetusEd</span>
        <span className="sidebar-subtitle">Admin Console</span>
      </div>

      <div className="sidebar-user">
        <p className="sidebar-user-name">{auth.user?.name}</p>
        <p className="sidebar-user-role">{auth.user?.role?.replace('_', ' ')}</p>
      </div>

      <nav className="sidebar-nav" aria-label="Admin navigation">
        {NAV_SECTIONS.map((section) => (
          <div key={section.title} className="sidebar-section">
            <h3 className="sidebar-section-title">{section.title}</h3>
            <ul className="sidebar-links">
              {section.links.map((link) => (
                <li key={link.to}>
                  <NavLink
                    to={link.to}
                    end={link.to === '/districts' || link.to === '/billing' || link.to === '/monitoring'}
                    className={({ isActive }) => isActive ? 'sidebar-link active' : 'sidebar-link'}
                  >
                    {link.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      <div className="sidebar-footer">
        <button className="sidebar-logout" onClick={handleLogout}>
          Sign Out
        </button>
      </div>
    </aside>
  );
};
